import {ColorPicker} from "primereact/colorpicker";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import React from "react";
import {DatasetChartProps} from "../../interfaces";

const DASH_OPTIONS = [
    {label: "Solid", value: "solid", strokeDasharray: "0"},
    {label: "Dotted", value: "dot", strokeDasharray: "2"},
    {label: "Dashed", value: "dash", strokeDasharray: "5"},
    {label: "Long Dashed", value: "longdash", strokeDasharray: "10"},
    {label: "Dash Dot", value: "dashdot", strokeDasharray: "15, 3, 3, 3"},
    {label: "Long Dash Dot", value: "longdashdot", strokeDasharray: "25, 3, 3, 3"},
];

type LineEditorProps = {
    onConfigChange: (config: DatasetChartProps) => void;
    setCurrentConfig: (config: DatasetChartProps) => void;
    currentConfig: DatasetChartProps;
};

type DashOption = {
    label: string;
    value: string;
    strokeDasharray: string;
}

function LineEditor({onConfigChange, setCurrentConfig, currentConfig}: LineEditorProps) {
    const customDropdownItem = (option: DashOption) => {
        return option ? (
            <div>
                <svg height="15" width="100">
                    <line
                        x1="0"
                        y1="50%"
                        x2="100%"
                        y2="50%"
                        stroke="black"
                        strokeWidth="2"
                        strokeDasharray={option.strokeDasharray}
                    />
                </svg>
            </div>
        ) : (
            <div>Select a dash</div>
        );
    };
    const colorBodyTemplate = (options) => {

        const currentColor = currentConfig.datasets ? currentConfig.datasets.find((dataset) => dataset.identifier === options.identifier).line
            ?.color : undefined;
        return (
            <ColorPicker
                id={options.identifier}
                value={currentColor}
                defaultColor="ffffff"
                onChange={(e) => handleConfigChange(options, e, "color")}
            />
        );
    };

    const widthBodyTemplate = (options) => {
        const currentWidth = currentConfig.datasets ? currentConfig.datasets.find((dataset) => dataset.identifier === options.identifier).line
            ?.width : undefined;
        return (
            <InputNumber
                placeholder="width"
                id={options.identifier}
                value={currentWidth}
                onChange={(e) => handleConfigChange(options, e, "width")}
                inputStyle={{width: "100%"}}
            />
        );
    };

    const dashBodyTemplate = (options) => {
        const currentDash = currentConfig.datasets ? currentConfig.datasets.find((dataset) => dataset.identifier === options.identifier).line
            ?.dash : undefined;
        return (
            <Dropdown
                className="w-full"
                id={options.identifier}
                placeholder="Select a dash"
                value={currentDash}
                options={DASH_OPTIONS}
                onChange={(e) => handleConfigChange(options, e, "dash")}
                itemTemplate={customDropdownItem}
                valueTemplate={customDropdownItem}
            />
        );
    };

    const rangeBodyTemplate = (options) => {
        const currentRange = currentConfig.datasets && currentConfig.datasets.find((dataset) => dataset.identifier === options.identifier).range;
        return (
            <div className="flex flex-row">
                <InputNumber
                    placeholder="min"
                    id={options.identifier}
                    value={currentRange ? currentRange[0] : undefined}
                    onChange={(e) => handleRangeChange(options, e, "min")}
                    inputStyle={{width: "100%"}}
                />
                <InputNumber
                    placeholder="max"
                    id={options.identifier}
                    value={currentRange ? currentRange[1] : undefined}
                    onChange={(e) => handleRangeChange(options, e, "max")}
                    inputStyle={{width: "100%"}}
                />
            </div>
        );
    };

    const handleRangeChange = (options, e, field): void => {
        const tempConfig = structuredClone(currentConfig);

        const datasetToUpdate = tempConfig.datasets ? tempConfig.datasets.find((dataset) => dataset.identifier === options.identifier) : undefined;
        if (!datasetToUpdate.range) {
            datasetToUpdate.range = [];
        }
        if (field === "min") {
            datasetToUpdate.range[0] = e.value;
        }
        if (field === "max") {
            datasetToUpdate.range[1] = e.value;
        }

        tempConfig.datasets = tempConfig.datasets ? tempConfig.datasets.map((dataset) => {
            if (dataset.identifier === options.identifier) {
                return datasetToUpdate;
            } else {
                return dataset;
            }
        }) : [];
        onConfigChange(tempConfig);
        setCurrentConfig(tempConfig);
    };

    const handleConfigChange = (options, e, field): void => {
        const tempConfig = structuredClone(currentConfig);

        const datasetToUpdate = tempConfig.datasets ? tempConfig.datasets.find((dataset) => dataset.identifier === options.identifier) : undefined;
        if (!datasetToUpdate.line) {
            datasetToUpdate.line = {};
        }
        datasetToUpdate.line[field] = e.value;

        tempConfig.datasets = tempConfig.datasets ? tempConfig.datasets.map((dataset) => {
            if (dataset.identifier === options.identifier) {
                return datasetToUpdate;
            } else {
                return dataset;
            }
        }) : [];
        onConfigChange(tempConfig);
        setCurrentConfig(tempConfig);
    };
    return (
        <DataTable value={currentConfig.datasets}>
            <Column field="identifier" header="Identifier" style={{width: "40%", padding: 0}}/>
            <Column field="color" header="Color" style={{width: "10%", padding: 0}} body={colorBodyTemplate}/>
            <Column field="width" header="Width" style={{width: "10%", padding: 0}} body={widthBodyTemplate}/>
            <Column field="dash" header="Dash" style={{width: "20%", padding: 0}} body={dashBodyTemplate}/>
            <Column field="range" header="Range" style={{width: "20%", padding: 0}} body={rangeBodyTemplate}/>
        </DataTable>
    );
}

export default LineEditor;
